<template>
  <div>
    <PageTagline :elements="tagline_elem" />
    <!-- Overlay di caricamento -->
    <CustomOverlay :show="overlay_show" text="Richiesta in corso, attendere...">

    <!-- Tutorial contestuale per gli argomenti del form. Vengono passati gli slug name dei video da mostrare -->
    <TutorialContestuale :video_to_show="['tutorial_rimborsi_interessi']"/>

      <!-- CARD PRINCIPALE DI CONTENIMENTO DI TUTTO IL FORM -->
      <b-card :title="form_name" class="mr-1 ml-1">

        <!-- MODALE CON INFO STATICHE PER L'UTENTE - SOSTITUISCE I BLOCCHI COLORATI IN CIMA AL FORM -->
        <b-modal ref="static-info-modal"
          title="Rimborso interessi - Note per l'utente"
          no-close-on-esc
          no-close-on-backdrop
          hide-header-close
          size='xl'>
          <p class="text-justify">
            Se hai acceso un mutuo, richiesto un prestito o un finanziamento, puoi utilizzare quest'area
            per ottenere il rimborso della <b>quota interessi</b> inclusa nella rata. L'importo massimo rimborsabile
            è pari al <b>90%</b> della quota interessi (potrai comunque richiedere un importo inferiore).
            Qualora tu non sia l'unico intestatario, indica l'importo complessivo ed il numero di intestatari:
            il sistema calcolerà in automatico la quota rimborsabile.
          </p>
          <p>Come funziona il calcolo dell'importo rimborsabile?</p>
          <p>Di seguito un esempio:</p>
          <b-table-simple small responsive class="text-center">
            <b-thead head-variant="light">
              <b-tr>
                <b-th>Quota capitale</b-th>
                <b-th>Quota interessi</b-th>
                <b-th>Numero di intestatari</b-th>
                <b-th>Percentuale rimborsabile</b-th>
                <b-th>Importo massimo rimborsabile</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr>
                <b-td>500 €</b-td>
                <b-td>100€</b-td>
                <b-td>1</b-td>
                <b-td>90%</b-td>
                <b-td>90,00 €</b-td>
              </b-tr>
              <b-tr>
                <b-td>500 €</b-td>
                <b-td>100€</b-td>
                <b-td>2</b-td>
                <b-td>45%</b-td>
                <b-td>45,00 €</b-td>
              </b-tr>
              <b-tr>
                <b-td>500 €</b-td>
                <b-td>100€</b-td>
                <b-td>3</b-td>
                <b-td>30%</b-td>
                <b-td>30,00 €</b-td>
              </b-tr>
              <b-tr>
                <b-td>500 €</b-td>
                <b-td>100€</b-td>
                <b-td>4</b-td>
                <b-td>22,5%</b-td>
                <b-td>22,50 €</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
          <hr>
          <p class="text-justify"><font-awesome-icon icon="calendar-alt"/> Per motivi di carattere fiscale la prima rata rimborsabile sarà quella che scade 1 mese dopo la data di inserimento della richiesta.</p>
          <p class="text-justify"><i>Esempio: il 10 Marzo posso richiedere il rimborso della rata che scade il 10 Aprile.</i></p>
          <hr>
          <p class="text-justify">
            <font-awesome-icon icon="info-circle"/> Il rimborso della quota interessi ti verrà accreditato direttamente
            sull'IBAN specificato nel modulo sottostante ( lo stesso IBAN su cui ti viene addebitato il mutuo, prestito o finanziamento ).
          </p>
          <template #modal-footer="{ cancel }">
            <b-button size="sm" variant="primary" @click="cancel()">
              Chiudi
            </b-button>
          </template>
        </b-modal>

        <b-form @submit.prevent="onSubmit">
          <!-- DATI DEL RIMBORSO INTERESI -->
          <b-card title="Dati del rimborso interessi" class="ml-1 mr-1 mt-2 mb-2">

            <!-- Oggetto del rimborso interessi ( id categoria )-->
            <b-form-group id="group-oggetto"
              label="* Oggetto del rimborso:"
              label-for="oggetto"
            >
              <b-form-select id="oggetto"
                v-model="form.id_categ"
                :options="categorie_rimborso"
                required></b-form-select>
            </b-form-group>

            <!-- Data prossima rata -->
            <b-form-group id="group-data-rata"
              label="* Data prossima rata:"
              label-for="data_rata">
              <b-form-datepicker
                  id="data_rata"
                  v-model="form.data_rata" class="mb-2"
                  :start-weekday="timepicker_it.start_weekday"
                  :min="data_rata_from"
                  v-bind="timepicker_it.labels"
                  required
                  ></b-form-datepicker>
            </b-form-group>

            <!-- Importo quota interessi -->
            <b-form-group id="group-importo-interessi"
              label="* Importo della quota interessi:"
              label-for="importo-interessi"
              description="Inserisci l'importo degli interessi sulla rata come da piano di ammortamento, in euro, IVA inclusa.">
              <b-input-group prepend="€">
                <b-form-input
                  id="importo_interessi"
                  v-model="form.importo_interessi"
                  @change="aggiorna_max_rimborsabile"
                  @keyup="check_euro_imp_interessi"
                  required
                ></b-form-input>
              </b-input-group>
            </b-form-group>

            <!-- Numero degli intestatari -->
            <b-form-group id="group-intestatari"
              label="* Numero intestatari:"
              label-for="intestatari"
              description="Inserisci il numero di intestatari del mutuo o prestito. Cambia questo valore se è cointestato.">
              <b-form-input
                id="intestatari"
                v-model="form.intestatari"
                @change="check_intestari"
                type="number"
                min="1"
                required
              ></b-form-input>
            </b-form-group>

            <!-- Importo max rimborsabile -->
            <b-form-group id="group-importo-max-rimb"
              label="* Importo massimo rimborsabile:"
              :invalidFeedback="invalid_max_rimb_helptext"
              label-for="importo-max-rimb">
              <b-input-group prepend="€">
                <b-form-input
                  id="importo_max_rimb"
                  v-model="form.importo_max_rimb"
                  :state="importo_max_rimb_state"
                  :disabled="true"
                  required
                ></b-form-input>
              </b-input-group>
            </b-form-group>

            <!-- Quantità da rimborsare ( parziale o massima ) -->
            <b-form-group id="group-quant-rimb"
              label="* Quantità da rimborsare:"
              label-for="quant_rimb"
            >
              <b-form-select id="quant_rimb"
                v-model="form.quant_rimb"
                :options="quantita_rimborsabile"
                @change="form.importo = form.importo_max_rimb"
                required></b-form-select>
            </b-form-group>

            <!-- Importo da rimborsare ( visibile solo se la quantita da rimborsare è parziale )
                 se è massima è pari appunto alla quantità massima rimborsabile viene impostata
                 dalla onchange della option qui sopra. Non può essere superiore alla quantià
                 max rimborsabile. -->
            <b-form-group v-if="show_importo_rimb" id="group-importo-rimb"
              label="* Importo da rimborsare:"
              :invalidFeedback="invalid_importo_helptext"
              label-for="importo-rimb">
              <b-input-group prepend="€">
                <b-form-input
                  id="importo_rimb"
                  v-model="form.importo"
                  @change="check_max_importo"
                  @keyup="check_euro_importo"
                  :state="importo_state"
                  required
                ></b-form-input>
              </b-input-group>
            </b-form-group>

            <!-- IBAN sul quale addebitare le rate -->
            <b-form-group id="group-iban"
              label="* Codice IBAN:"
              label-for="iban"
              description="Inserisci il codice IBAN sul quale vengono addebitate le rate. Iniziando a digitare, ti verranno presentati gli IBAN già utilizzati in passato."
              :invalidFeedback="invalid_iban_helptext">
              <b-form-input
                id="iban"
                v-model="form.iban"
                @keyup="ricerca_iban_utente"
                placeholder="Codice IBAN"
                list="iban-list"
                type="text"
                :state="iban_state"
                required
              ></b-form-input>

              <!--La datalist contiene i valori tirati su per l'autocomplete al keyup sulla input box -->
              <!--La imput box che la sua deve avere l'attributo "list" settato all'id della datalist -->
              <datalist id="iban-list">
                <option v-for="(iban, index) in user_iban_datalist" :key="index">{{ iban }}</option>
              </datalist>
            </b-form-group>

            <!-- Modale per caricamento del documento di addebito dell'ultima rata -->
            <b-form-group id="group-file-doc-rata"
                description="Allega l'estratto conto o la contabile di pagamento relativi alla rata più recente."
                label="* Documento addebito ultima rata:">

              <!-- Modale per upload file del reventivo -->
              <FileUploadModal :file_kind="file_upload_modals.file_rata.file_kind"
                               @fileIdReady="assegna_id_file"
                               @fileRemoved="rimuovi_id_file"/>
            </b-form-group>

            <!-- Modale per caricamento del documento relativo al piano di ammortamento -->
            <b-form-group id="group-file-p-ammor"
                description="Allega il documento ufficiale rilasciato dall'istituto finanziario."
                label="* Piano di ammortamento:">

              <!-- Modale per upload file del reventivo -->
              <FileUploadModal :file_kind="file_upload_modals.file_p_ammor.file_kind"
                                @fileIdReady="assegna_id_file"
                                @fileRemoved="rimuovi_id_file"/>
            </b-form-group>

          </b-card>

          <!-- Pulsante di invio -->
          <b-button type="submit" variant="primary" class="mr-1">Invia richiesta</b-button>

        </b-form>

      </b-card>
    </CustomOverlay>
  </div>
</template>

<script>
import PageTagline from '@/components/PageTagline'
import CustomOverlay from '@/components/CustomOverlay'
import FileUploadModal from '@/components/FileUploadModal'
import TutorialContestuale from '@/components/TutorialContestuale'
import { generalLib } from '@/generalLib'
import { apiUrl } from '@/apiUrl'
import axios from 'axios'
import iban from 'iban'

export default {
  name: 'RimborsoInteressi',
  data () {
    return {
      // Variabili generali
      timepicker_it : generalLib.timepicker_it,
      tagline_elem : [ { text : "Rimborso Interessi", link : 'rimb-interessi' } ],
      form_name : "Rimborso interessi",
      form_kind : 3, // il kind del rimborso interessi
      overlay_show: false, // overlay con spinner per indicare che il sistema sta lavorando

      // Categorie di rimborso selezionabili
      categorie_rimborso : [
        { value: null, text: "Seleziona un'opzione", disabled: true }
      ],

      // Iban dell'utente ricercati al keyup sul campo iban
      user_iban_datalist : [],

      // Le due opzione della quantità rimborsabile
      quantita_rimborsabile : [
        { value: "massima", text: "Massima" },
        { value: "parziale", text: "Parziale" }
      ],

      // Dati del form
      form : {
        id_categ : null,
        data_rata : null,
        importo_interessi : null,
        intestatari : 1,
        importo_max_rimb : 0,
        quant_rimb : "massima",
        importo : null,
        iban : null,
        // Array con gli id dei files da associare all'acquisto
        docs : []
      },

      // Stato del codice iban per notificare la non validità
      iban_state : null,

      // Stato dell'importo massimo rimborsabile per la non validità
      importo_max_rimb_state : null,

      // Stato dell'importo definitivo per la non validità
      importo_state : null,

      // Dati per inizilizzare la modale di caricamento del file della rata
      file_upload_modals : {
        file_rata : { file_kind : 'doc_rata', doc_id : null },
        file_p_ammor : { file_kind : 'doc_loan', doc_id : null }
      }

    }
  },
  computed : {
    /**
     * Torna True o False a seconda che la casella di input relativa al valore
     * form.importo_rimb debba essere visibile o meno.
     * Ciò dipende dal valore form.quant_rimb, solo se la quantità da rimborsare
     * è parziale l'input deve essere visibile.
     */
    show_importo_rimb : function () {
      if ( this.form.quant_rimb === 'parziale' ){
        return true;
      }
      return false;
    },
    /**
     * Retituiamo la data dal quale deve partire il timepicker per la data della
     * prossima data. Cioè dopo un mese da oggi.
     */
    data_rata_from : function () {
      let d = new Date();
      d.setMonth( d.getMonth() + 1 );
      return d;
    },
    /**
     * Se lo stato dell'iban non è corretto visualizziamo il testo di help
     * per l'utente.
     */
    invalid_iban_helptext : function () {
      if ( this.iban_state === false ){
        return 'Codice IBAN non valido';
      } else {
        return '';
      }
    },
    /**
     * Se lo stato dell'import massimo rimborsabile non è valido lo notifichiamo
     * all'utente.
     */
    invalid_max_rimb_helptext : function () {
      if ( this.importo_max_rimb_state === false && this.form.quant_rimb === 'massima' ) {
        let credito = this.$store.state.user.user_credito.totale;
        let mess = "L'importo massimo rimborsabile calcolato supera la tua attuale disponibilità welfare che è di " +
                   credito + " euro, non sarai in grado di completare la richiesta. Modifica il valore dell'importo della quota " +
                   "interessi o degli intestatari, od opta per un rimborso parziale.";
        return mess;
      } else {
        return '';
      }
    },
    invalid_importo_helptext : function () {
      if ( this.importo_state === false && this.form.quant_rimb === 'parziale' ) {
        let credito = this.$store.state.user.user_credito.totale;
        let mess = "L'importo digitato supera la tua attuale disponibilità welfare che è di " +
                   credito + "euro, non sarai in grado di completare la richiesta.";
        return mess;
      } else {
        return '';
      }
    }
  },
  methods : {
    /**
     * Esecuzione dell'acquisto
     */
    onSubmit : function () {

      // Se i documenti decessari non sono presenti torniamo senza fare nulla
      if ( !this.compila_array_docs() ) return;

      // Se i documenti ci sono possiamo inviare i dati!
      let data_to_send = Object.assign( {}, this.form );

      generalLib.acquisto_confirm_message_box (  this, this.form.importo, 'totale' )
      .then( ( value ) => {
        // L'utente ha risposto Annulla.
        if ( !value ) return;

        // Se invece l'utente decide di proseguire attivo
        // l'overlay e poi eseguo il post delle info.
        this.overlay_show = true;

        axios.post( apiUrl.welfareapi.create_acq_rimbint , data_to_send )
        .then( (response) => {

          // Visualizza la finestra di completamento, la quale alla chiusura ci riporta
          // alla home.
          generalLib.acquisto_complete_message_box( this, response.data.id_acquisto )
          .then ( ( val ) => {
              this.$router.push( { name : 'menu'} );
          });

        })
        .catch( (error) => {
            this.overlay_show = false;
            generalLib.backend_error_message_box( this, error.response.data );
        });

      })
      .catch( ( error ) => {
        console.error( error );
      });

    },
    /**
     * Verifica la presenza dei files che sono richiesti e compila l'array docs con
     * gli id degli stessi.
     * Torna true se tutti i files che devono essere presenti ci sono altrimenti torna
     * false.
     */
    compila_array_docs : function () {

      // Puliamo prima l'array
      this.form.docs = new Array();

      // Il file relativo all'ultima rata è richiesto.
      if ( this.file_upload_modals.file_rata.doc_id === null ) {
        generalLib.backend_error_message_box( this, "E' necessario caricare il file del documento di addebito dell'ultima rata per proseguire" );
        return false;
      }
      this.form.docs.push( this.file_upload_modals.file_rata.doc_id );

      // Il file del piano di ammortamento è richiesto.
      if ( this.file_upload_modals.file_p_ammor.doc_id === null ) {
        generalLib.backend_error_message_box( this, "E' necessario caricare il file relativo al piano di ammortamento per proseguire" );
        return false;
      }
      this.form.docs.push( this.file_upload_modals.file_p_ammor.doc_id );

      return true;

    },
    /**
     * Va a ricercare gli IBAN mentre l'utente va a digitare sul form per l'autocomplete.
     * Limito gli eventuali risultati a 10 elementi.
     * Non Consento l'inserimento di iban con spazi o caratteri separatori e lo rendo tutto
     * maiuscolo.
     */
    ricerca_iban_utente : function () {

      // Tolgo tutti i caratteri che non sono lettere o numeri e lo rendo maiuscolo.
      this.form.iban = this.form.iban.replace(/[^A-Za-z0-9]/ig,'').toUpperCase();

      // Inizio a cercare dal secondo carattere in poi
      if ( this.form.iban.length < 2 ) return;

      let api_url = apiUrl.welfareapi.search_iban_utente.replace( '@iban', this.form.iban );
      axios.get( api_url, { params: { limit : 10 } } )
      .then( (response) => {
        this.user_iban_datalist = []
        for ( let elem of response.data ) {
          this.user_iban_datalist.push( elem.iban );
        }
      })
      .catch ( ( error ) => {
        console.error( error.response.data );
      });
    },
    /**
     * Aggiorna il valore dell'import massimo rimborsabile sulla base dell'importo degli interessi e del numero
     * degli intestatari.
     */
    aggiorna_max_rimborsabile : function () {
      if ( this.form.importo_interessi === null ) return;
      let res = ( ( this.form.importo_interessi / 100 ) * 90 ) / this.form.intestatari;
      // Arrotondiamo alla seconda cifra decimale.
      this.form.importo_max_rimb = Math.round ( res * 100 ) / 100;
      // Aggiorniamo anche l'importo finale ponendolo uguale a quello massimo
      this.form.importo = this.form.importo_max_rimb;
    },
    /**
     * Controlla che il formato in euro sia corretto per l'importo interessi
     */
    check_euro_imp_interessi : function () {
      if ( !generalLib.euro_is_valid( this.form.importo_interessi ) ){
        this.form.importo_interessi = this.form.importo_interessi.slice(0,this.form.importo_interessi.length - 1);
      }
    },
    /**
     * Fa in modo che il numero di intestatari non sia mai zero ed aggiorna il massimo
     * rimborsabile.
     */
    check_intestari : function () {
      if ( this.form.intestatari < 1 ) this.form.intestatari = 1
      this.aggiorna_max_rimborsabile();
    },
    /**
     * Se la quantità da rimborsare è parziale e quindi possiamo editare l'importo,
     * controlla che il numero non ecceda il massimo rimborsabile.
     */
    check_max_importo : function () {
      if ( this.form.importo > this.form.importo_max_rimb ) {
        this.form.importo = this.form.importo_max_rimb;
      }
    },
    /**
     * Se la quantità da rimborsare è parziale e quindi possiamo editare l'importo,
     * controlla che il campo in euro sia corretto.
     */
    check_euro_importo : function () {
      if ( !generalLib.euro_is_valid( this.form.importo ) ){
        this.form.importo = this.form.importo.slice(0,this.form.importo.length - 1);
      }
    },
    /**
     * Chiamata quando le modali di upload dei files restituiscono dati,
     * assegna all'oggetto interno dedicato ai files i valori tornati.
     */
    assegna_id_file : function ( data ) {
      if ( data.file_kind === 'doc_rata' ) {
        this.file_upload_modals.file_rata.doc_id = data.doc_id;
      }
      if ( data.file_kind === 'doc_loan' ) {
        this.file_upload_modals.file_p_ammor.doc_id = data.doc_id;
      }
    },
    /**
     * Chiamata quando le modali di upload dei files tolgono via i documenti
     * rimuove gli id dall'oggetto interno del form.
     */
    rimuovi_id_file : function ( data ) {
      if ( data.file_kind === 'doc_rata' ) {
        this.file_upload_modals.file_rata.doc_id = null;
      }
      if ( data.file_kind === 'doc_loan' ) {
        this.file_upload_modals.file_p_ammor.doc_id = null;
      }
    },
    /**
     * Se stiamo operando in versione replay ( la prop id_acq_replay è settata ), va
     * a prendere i dati da usare per questo nuovo acquisto da un acquisto passato il cui
     * id viene passato dal router e posto per l'appunto nella prop id_acq_replay.
     */
    get_replay_data : function () {

      if ( this.id_acq_replay === null ) return;

      let api_url = apiUrl.welfareapi.acq_info_full.replace( '@id_acquisto', this.id_acq_replay );

      axios.get( api_url )
      .then( (response) => {

        // per qualche motivo l'acquisto non ha il kind giusto, non possiamo popolare il form.
        if ( response.data.kind != this.form_kind ) return;

        this.form.id_categ = response.data.id_categ_id;

        // La data della rata la faccio mettere all'utente, è una data che deve essere
        // almeno due mesi avanti rispetto ad oggi e se prendo un acquisto vecchio
        // possiamo andare incontro ad un errore.
        //this.form.data_rata = response.data.note.note.data_prox_rata;

        this.form.importo_interessi = response.data.note.note.quota_interessi;
        this.form.intestatari = response.data.note.note.intest_mutuo;
        this.form.iban = response.data.note.note.iban;

        // Settiamo la quantità da rimborsare se massima o parziale, dal
        // backend abbiamo 'part' per parziale e 'max' per massima.
        let tot_loan = response.data.note.note.tot_loan;
        this.form.quant_rimb = tot_loan === 'part' ? 'parziale' : 'massima';

        // Una volta caricati i dati andiamo ad aggiornare il massimo rimborsabile sulla base
        // di quanto inserito nel form
        this.aggiorna_max_rimborsabile();

      })
      .catch( (error) => {
        generalLib.backend_error_message_box( this, error.response.data );
      })
    }
  },
  watch : {

    /**
     * Siccome la validazione dell'importo dipende non solo dalla cifra ma anche dal tipo di quantità
     * rimborsata, dovendo tenere sotto controllo la modifica di due valori contemporanei opto per
     * validare i campi tutti insieme controllando direttamente la modifica dell'oggetto form.
     */
    form: {
      handler( val ){
        // Validazione IBAN
        if ( val.iban !== null ){
          if ( val.iban === '' ) {
            this.iban_state = null;
          } else if ( iban.isValid( val.iban ) ) {
            this.iban_state = true;
          } else {
            this.iban_state = false;
          }
        }

        // Validazione max importo rimborsabile
        if ( val.importo_max_rimb !== '' ) {
          let max_rimb_val = parseFloat( val.importo_max_rimb );
          if ( max_rimb_val > this.$store.state.user.user_credito.totale && this.form.quant_rimb === 'massima' ){
            this.importo_max_rimb_state = false;
          } else {
            this.importo_max_rimb_state = null;
          }
        }

        // Validazione dell'importo finale se stiamo optando per il rimborso parziale
        if ( val.importo !== null && val.importo !== '' ) {
          let importo_val = parseFloat( val.importo );
          if ( importo_val > this.$store.state.user.user_credito.totale && this.form.quant_rimb === 'parziale' ){
            this.importo_state = false;
          } else {
            this.importo_state = null;
          }
        }

      },
      deep: true
    }
  },
  /**
   * Alla creazione del componente prendiamo dal backend i nomi e gli id delle possibile categorie
   * di acquisto.
   * Se siamo in modalità replay ( è stata passata la prop id_acq_replay con l'id dell'acquisto ) allora
   * il form viene popolato con tutti i dati che è possibile caricare.
   */
  created : function () {
    let api_url = apiUrl.welfareapi.acq_categ_kind.replace( '@kind', this.form_kind );
    axios.get( api_url )
      .then( (response) => {
        for ( let elem of response.data ) {
          this.categorie_rimborso.push( { value : elem.id, text : elem.nome } );
        }

        // Se siamo in modalità replay carichiamo nel form i dati dell'acquisto
        this.get_replay_data();
    })
    .catch( (error) => {
        let mess = "Si è verificato un errore nel caricamento degli oggetti del rimborso, " +
                   "non sarà possibile concludere correttamente la procedura. " +
                   "Tornare alla homepage e riprovare ad aprire il form. Se il problema persiste " +
                   "contattare l'assistenza."
        generalLib.backend_error_message_box( this, mess );
     })
  },
  /**
   * Quando la pagina è pronta visualizziamo la modale informativa per il servizio.
   */
  mounted : function () {
    this.$refs['static-info-modal'].show();
  },
  props : {
    /**
    * Prop che è passata dal router, se valorizzata indica l'acquisto da replicare
    * inserendo per l'utente i dati nel form.
    */
    id_acq_replay: {
      type: [String, Number],
      default : null
    }
  },
  components: {
    PageTagline,
    FileUploadModal,
    TutorialContestuale,
    CustomOverlay
  }

}

</script>


<style scoped>
/* Vedi il form della previdenza integrativa per la spiegazione di questi due
   workaround*/
#group-importo-max-rimb :deep( .invalid-feedback ) {
  display: block;
}
#group-importo-rimb :deep( .invalid-feedback ) {
  display: block;
}
</style>